import WebComponent from '../../webComponents/WebComponent';

const FRAGMENTS = {
  DECREMENT: '[data-fragment="decrement"]',
  INCREMENT: '[data-fragment="increment"]',
  INPUT: '[data-fragment="input"]',
};

const getNumberOfDecimals = (number) => {
  const numberBag = number.toString().replace(',', '.').split('.');
  const decimals = numberBag[1];
  return decimals ? decimals.length : 0;
};

const floatValidChar = /^[0-9]|,|\.|-$/;
// const integerValidChar = /^[0-9]|,|\.|-$/;
const integerValidChar = /^[0-9]|-$/;

const fixDecimals = (value, decimals) => {
  return parseFloat((parseFloat(value)).toFixed(decimals));
};
const normalizeValue = ({ value, step, max, min, defaultValue = 0, allowLimits }) => {
  let maxReached = false;
  let minReached = false;
  let normalizedValue;

  const numberValue = value.toString().trim().replace(/,/g, '.');
  const decimals = getNumberOfDecimals(step);
  normalizedValue = fixDecimals(numberValue === '' ? defaultValue : numberValue, decimals);
  if (Number.isNaN(normalizedValue)) {
    normalizedValue = defaultValue;
  }
  const isDividedWithStep = normalizedValue % step === 0;
  if (!isDividedWithStep) {
    normalizedValue = fixDecimals(Math.round(normalizedValue / step) * step, decimals);
  }
  const fixedMin = allowLimits ? min : fixDecimals(Math.round(min / step) * step, decimals);
  const fixedMax = allowLimits ? max : fixDecimals(Math.round(max / step) * step, decimals);

  // console.log('normalizedValue', normalizedValue, 'min', min, 'fixedMin', fixedMin);
  if (min != null && normalizedValue <= fixedMin) {
    normalizedValue = Math.max(normalizedValue, fixedMin);
    minReached = true;
  }
  if (max != null && normalizedValue >= fixedMax) {
    normalizedValue = Math.min(normalizedValue, fixedMax);
    maxReached = true;
  }
  return {
    maxReached,
    minReached,
    normalizedValue,
  };
};


class NumberInput extends WebComponent {
  constructor(props) {
    super(props);
    this.fragments = FRAGMENTS;
    const { step } = this.props;
    this.state = {
      decimals: getNumberOfDecimals(step),
      focused: false,
      value: null,
      normalizedValue: null,
      maxReached: false,
      minReached: false,
    };
    const { min, max } = this.props;
    if (min > max) {
      throw new Error('NumberInput: max is less than min');
    }
  }

  didMount() {
    const { messageTarget, buttons } = this.props;

    this.messageEl = document.querySelector(messageTarget);
    if (buttons) {
      this.on('click', FRAGMENTS.DECREMENT, this.handleDecrementClick);
      this.on('click', FRAGMENTS.INCREMENT, this.handleIncrementClick);
    }
    this.on('input', FRAGMENTS.INPUT, this.handleInputChange);
    this.on('change', FRAGMENTS.INPUT, this.handleInputEventChange);
    this.on('focus', this.handleFocus);
    this.on('blur', this.handleBlur);
    this.on('blur', FRAGMENTS.INPUT, this.handleInputBlur);

    const inputValue = this.elements.INPUT.value;
    const { normalizedValue, ...valueParams } = normalizeValue({
      ...this.props,
      value: inputValue,
    });
    this.setState({
      value: this.willBeEmpty(inputValue) ? '' : normalizedValue,
      normalizedValue,
      ...valueParams,
    });
  }

  handleInputEventChange = (e) => {
    e.stopPropagation();
    e.preventDefault();
  }
  handleFocus = () => {
    this.setState({
      focused: true,
    });
    window.addEventListener('keydown', this.handleKeyPress);
    window.addEventListener('wheel', this.handleMouseWheel);
  };
  handleBlur = () => {
    this.setState({
      focused: false,
    });
    window.removeEventListener('keydown', this.handleKeyPress);
    window.removeEventListener('wheel', this.handleMouseWheel);
  };
  handleKeyPress = (e) => {
    const { which, ctrlKey, key } = e;
    const isFunctionKey = which >= 0 && which <= 46 && which !== 32; // spacebar
    if (!isFunctionKey && !ctrlKey) {
      const { decimals, value } = this.state;
      const pattern = decimals === 0 ? integerValidChar : floatValidChar;
      const result = pattern.test(key);
      if (!result) {
        e.preventDefault();
      }
      // minus only on start
      if (key === '-' && value.length > 0) {
        e.preventDefault();
      }
      // comma or dot only once
      if ((key === '.' || key === ',') && (value.toString().indexOf('.') !== -1 || value.toString().indexOf(',') !== -1)) {
        e.preventDefault();
      }
    }
    if (e.keyCode === 38) {
      e.preventDefault();
      this.incrementOrDecrementValue();
    }
    if (e.keyCode === 40) {
      e.preventDefault();
      this.incrementOrDecrementValue(true);
    }
    if (e.keyCode === 13) {
      const { normalizedValue } = this.state;
      this.setState({
        value: normalizedValue,
      });
    }
  };
  handleInputBlur = () => {
    if (!this.willBeEmpty()) {
      const { normalizedValue } = this.state;
      this.setState({
        value: normalizedValue,
      });
    }
  };
  incrementOrDecrementValue(decrement) {
    const { normalizedValue, value } = this.state;
    const { step } = this.props;
    const { normalizedValue: newValue, ...valueParams } = normalizeValue({
      ...this.props,
      value: value === '' ? normalizedValue : (decrement ? normalizedValue - step : +normalizedValue + step),
    });
    this.setState({
      value: newValue,
      normalizedValue: newValue,
      ...valueParams,
    });
  }
  handleDecrementClick = () => {
    this.incrementOrDecrementValue(true);
  };
  handleIncrementClick = () => {
    this.incrementOrDecrementValue();
  };
  handleInputChange = (e) => {
    const { value } = e.currentTarget;
    const { normalizedValue, ...valueParams } = normalizeValue({
      ...this.props,
      value,
    });
    this.setState({
      value,
      normalizedValue,
      ...valueParams,
    });
    e.stopPropagation();
  };
  handleMouseWheel = (e) => {
    if (e.deltaY < 0) {
      this.incrementOrDecrementValue();
    } else {
      this.incrementOrDecrementValue(true);
    }
  };
  setValue = (value) => {
    this.setState({
      value,
      normalizedValue: value,
    });
  };
  willUnMount() {
    window.removeEventListener('keydown', this.handleKeyPress);
    window.removeEventListener('wheel', this.handleMouseWheel);
  }
  willBeEmpty(value) {
    if (value == null) {
      ({ value } = this.state);
    }
    const { allowEmpty } = this.props;
    return allowEmpty && value.toString().trim() === '';
  }
  update(prevProps, prevState, inDocument) {
    const { value, maxReached, minReached, normalizedValue, buttons } = this.state;
    const { value: prevValue } = prevState;
    const { disabled, readonly, allowEmpty } = this.props;
    if (inDocument) {
      this.elements.INPUT.value = value;
      this.elements.INPUT.disabled = disabled;
      this.elements.INPUT.readonly = readonly;
      if (buttons) {
        this.elements.INCREMENT.disabled = readonly || disabled || (maxReached && value !== '');
        this.elements.DECREMENT.disabled = readonly || disabled || (minReached && value !== '');
      }
    }
    if (this.messageEl) {
      if (!this.willBeEmpty() && prevValue !== value && value.toString().trim() !== normalizedValue.toString()) {
        this.messageEl.innerHTML = this.i18n('willBeUsed', { normalizedValue });
      } else {
        this.messageEl.innerHTML = '';
      }
    }
    if (inDocument && prevValue !== value && ((value.toString() === normalizedValue.toString()) || (allowEmpty && value.toString() === ''))) {
      this.dispatchChangeEvent(normalizedValue);
    }
  }
}

NumberInput.fragments = FRAGMENTS;
NumberInput.defaultProps = {
  step: 1, // povolené krokování. úpokud není desetinné, tak nepovolí vložit desetinná čísla
  max: null, // omezení maxima
  min: null, // omezení minima
  allowEmpty: true, // povolí input vyplnit prázdný. Pokud je false, tak vyplní defaultValue
  allowLimits: false, // dovolí vložit max, nebo min ikdyž není dělitelné step
  messageTarget: null, // selector elementu, ktde se má zobrazit zpráva, pokud hodnota není validní - tj. byla zadány mimo validní rozsah nebo není dělitelná step.
  defaultValue: 0, // pokud se vyplní prázdný řetězec, dojde k chybě, tak se použije defaultValue. Nevloží se defaultValue, ale dopočítané nejbliší číslo k defaultValue odpovídající kritériím step, max a min
  disabled: false, // disablovaná celá komponenty - nejde zapisovat ani číst
  readonly: false, // z komponenty lze zkopírovat hodnotu, ale nelze hodnotu měnit
  buttons: true, // budou se používat increment/decrement tlačítka?
  messages: {
    willBeUsed: 'Bude použita hodnota "${normalizedvalue}"',
  },
};

export default NumberInput;




// WEBPACK FOOTER //
// ./node_modules/fg-frontend/src/components/NumberInput/NumberInput.js